import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Palmtree, X } from 'lucide-react';
import { useRestaurantStatus } from '../hooks/useRestaurantStatus';

// Routes where the public vacation pop-up must never appear (staff / utility
// screens). Everything else is a customer-facing page and should show it.
const HIDDEN_PREFIXES = ['/admin', '/kitchen', '/print'];

// Format an ISO "YYYY-MM-DD" string as Hungarian "2026. 09. 20.". Returns an
// empty string for missing/invalid input so the UI can degrade gracefully.
const formatHu = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('hu-HU', { year: 'numeric', month: '2-digit', day: '2-digit' });
};

const VacationModal = () => {
  const location = useLocation();
  const { loaded, vacationEnabled, vacationStart, vacationEnd } = useRestaurantStatus();
  const [dismissed, setDismissed] = useState(false);

  // A stable key per date-range so dismissing does not hide a *new* vacation
  // period announced later. sessionStorage → shows again on the next visit.
  const storageKey = `bb_vacation_dismissed_${vacationStart || 'x'}_${vacationEnd || 'x'}`;

  useEffect(() => {
    if (!vacationEnabled) return;
    try {
      if (sessionStorage.getItem(storageKey) === '1') {
        setDismissed(true);
      } else {
        setDismissed(false);
      }
    } catch (e) {
      // sessionStorage can throw in private mode — just show the pop-up.
      setDismissed(false);
    }
  }, [vacationEnabled, storageKey]);

  const onHiddenRoute = HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p));

  if (!loaded || !vacationEnabled || dismissed || onHiddenRoute) {
    return null;
  }

  const close = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(storageKey, '1');
    } catch (e) {
      /* ignore */
    }
  };

  const start = formatHu(vacationStart);
  const end = formatHu(vacationEnd);

  return (
    <>
      {/* Backdrop with blur */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] transition-opacity duration-300"
        onClick={close}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
        <div
          className="relative bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 animate-fadeIn"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={close}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Bezárás"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>

          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-gradient-to-br from-[#00BCD4] to-[#00ACC1] rounded-full flex items-center justify-center shadow-lg">
              <Palmtree className="w-10 h-10 text-white" />
            </div>
          </div>

          {/* Title */}
          <h2 className="text-2xl font-black text-gray-900 text-center mb-4">
            Szabadságon vagyunk 🌴
          </h2>

          {/* Message */}
          <div className="text-center space-y-4 mb-6">
            <p className="text-gray-700 leading-relaxed text-lg">
              Kedves Vendégeink! Pihenőt tartunk, ezért a büfé átmenetileg zárva tart.
            </p>
            {(start || end) && (
              <div className="inline-block px-5 py-3 bg-[#00BCD4]/10 border-2 border-[#00BCD4] rounded-xl">
                <p className="text-sm text-gray-600 mb-1">Szabadság időtartama:</p>
                <p className="text-lg font-bold text-[#00838F]">
                  {start && end ? `${start} – ${end}` : start || end}
                </p>
              </div>
            )}
            <p className="text-gray-600">
              Köszönjük a türelmet, hamarosan újra várunk szeretettel!
            </p>
          </div>

          {/* Button */}
          <button
            onClick={close}
            className="w-full px-6 py-3 bg-gradient-to-r from-[#00BCD4] to-[#00ACC1] text-white rounded-xl font-bold hover:shadow-lg transition-all"
          >
            Rendben
          </button>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default VacationModal;
